export class CheckoutTracking {
	constructor() {
	  this.cartData = null;
	  this.isFetching = false;
	}
	
	async init() {
		this.cartData = await this.fetchCartData();
		document.addEventListener('tracking:shipping-info', (event) => this.addShippingInfo(event));
		document.addEventListener('tracking:payment-info', (event) => this.addPaymentInfo(event));
	}
  
	_gtag(...args) {
		if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
		  const [command, eventName, params] = args;
		  if (command === "event" && eventName) {
			window.dataLayer.push({
			  event: eventName,
			  ...params
			});
		  } else {
			window.dataLayer.push({ command, ...params });
		  }
		}
	}
	  
  
	_event(name, params = {}) {
	  this._gtag('event', name, params);
	}
	
	async fetchCartData() {
		if (this.cartData !== null || this.isFetching) {
			return this.cartData;
		}
		this.isFetching = true;
		try {
			const response = await fetch(window.Shopify.routes.root + 'cart.js');
			const data = await response.json();
			this.cartData = data;
			return this.cartData;
		} catch (error) {
			console.error("Error fetching cart data:", error);
			return null;
		} finally {
			this.isFetching = false;
		}
	}
  
	async beginCheckout() {
		try {
			const cartDataJson = this.cartData;
			if(!cartDataJson) throw new Error("Cart data not found");
			const payload = { 
				currency: cartDataJson.currency,
				value: cartDataJson.total_price ? (parseFloat(cartDataJson.total_price) / 100) : undefined,
				coupon: cartDataJson?.cart_level_discount_applications?.length
				? cartDataJson?.cart_level_discount_applications[0]?.title
				: "",
				discount: window.checkout?.discountAmount ? parseFloat((parseFloat(window.checkout.discountAmount) / 100).toFixed(2)) : 0,
				items: this.constructItemPayload(cartDataJson)
			};
			this._event('begin_checkout', payload);
		} catch (error) {
			console.error("Error occured at beginCheckout tracking:", error);
		}
	}

	constructItemPayload(data) {
		if(!data) return;
		const payload = data?.items?.map((item, index) => {
			return {
				item_id: item?.sku ? `SKU_${item.sku}` : item.variant_id,
				item_name: item?.product_title ? item?.product_title : item?.title,
				affiliation: `Wellvita ${Theme.settings?.country_iso_code || ''}`,
				coupon: item?.discounts?.length
				? item?.discounts[0]?.title
				: "",
				discount: item?.discounts?.length
				? item?.discounts[0]?.amount ? (parseFloat(item?.discounts[0]?.amount) / 100) : 0
				: 0,
				index: index,
				item_brand: item?.vendor,
				item_category: item?.product_type ? item?.product_type : "Uncategorized",
				item_list_id: "cart",
				item_list_name: "Cart",
				item_variant: item?.variant_title,
				price: item.price ? (parseFloat(item.price) / 100) : undefined,
				quantity: item.quantity
			}
		})
		return payload;
	}
  
	addShippingInfo() {
		try {
			const cartDataJson = this.cartData;
			const deliveryPrice = this.getDeliveryPrice(window.checkout.deliveryPrice);
			if(!cartDataJson) throw new Error("Cart data not found");
			const payload = { 
				currency: cartDataJson.currency,
				value: deliveryPrice,
				coupon: window.checkout.manualDiscountCode,
				shipping_tier: window.checkout.deliveryMethod,
				discount: window.checkout?.discountAmount ? parseFloat((parseFloat(window.checkout.discountAmount) / 100).toFixed(2)) : 0,
				items: this.constructItemPayload(cartDataJson)
			};
			this._event('add_shipping_info', payload);
		} catch (error) {
			console.error("Error occured at addShippingInfo tracking:", error);
		}
	}
  
	addPaymentInfo() {
		try {
			let fullPrice;
			const cartDataJson = this.cartData;
			if(window.checkout.totalPrice) fullPrice = parseFloat(window.checkout.totalPrice) / 100;
			if(!cartDataJson) throw new Error("Cart data not found");
			const payload = { 
				currency: cartDataJson.currency,
				value: fullPrice,
				coupon: window.checkout.manualDiscountCode,
				payment_type: window.checkout.paymentMethod,
				discount: window.checkout?.discountAmount ? parseFloat((parseFloat(window.checkout.discountAmount) / 100).toFixed(2)) : 0,
				items: this.constructItemPayload(cartDataJson)
			};
			this._event('add_payment_info', payload);
		} catch (error) {
			console.error("Error occured at addPaymentInfo tracking:", error);
		}	
	}

	getDeliveryPrice(deliveryPrice) {
		const isFreeDeliveryApplied = window.checkout.isFreeDeliveryApplied || false;
		if(isFreeDeliveryApplied) return 0;
		if(!deliveryPrice) return 0;
		return parseFloat(deliveryPrice) / 100;
	}

	purchase(orderId) {
		if (!orderId) {
			console.warn("Skipping purchase tracking: missing orderId");
			return;
		}

	  try {
		let checkoutData = window.checkout;
		if (!checkoutData) {
		  const stored = sessionStorage.getItem('checkout_tracking_data');
		  if (stored) {
				try {
					checkoutData = JSON.parse(stored);
				} catch (error) {
					console.error("Failed to parse checkout_tracking_data from sessionStorage:", error);
					checkoutData = null;
				}
		  } else {
			checkoutData = null;
		  }
		}
		
		// Get cart data from storage or this.cartData
		let cartData = this.cartData;
		if (!cartData || !cartData.items || cartData.items.length === 0) {
		  const stored = sessionStorage.getItem('checkout_cart_data');
		  if (stored) {
				try {
					cartData = JSON.parse(stored);
				} catch (error) {
					console.error("Failed to parse checkout_cart_data from sessionStorage:", error);
					cartData = null;
				}
		  } else {
			cartData = null;
		  }
		}
		
		if (!checkoutData || !cartData) {
		  console.warn("Missing checkout or cart data for purchase tracking");
		  return;
		}
	
		const payload = {
		  transaction_id: orderId,
		  value: checkoutData.totalPrice ? parseFloat(checkoutData.totalPrice) / 100 : undefined,
		  tax: checkoutData.vatPrice ? parseFloat(checkoutData.vatPrice) / 100 : undefined,
		  shipping: checkoutData.deliveryPrice ? parseFloat(checkoutData.deliveryPrice) / 100 : undefined,
		  currency: checkoutData.currency || Theme.settings?.shop_currency || "DKK",
		  coupon: checkoutData.discountCode || undefined,
		  discount: checkoutData.discountAmount ? parseFloat(checkoutData.discountAmount) / 100 : 0,
		  items: this.constructItemPayload(cartData) || []
		};
	
		this._event('purchase', payload);
	  } 
	  catch (error) {
			console.error("Error occured at purchase tracking:", error);
	  }
	}
}
  
const checkoutTracking = new CheckoutTracking();
  
